import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PIPELINE_STAGES } from '@tipstrack/shared';
import { fetchIndeedStatus, fetchPipelineStats, lastRunsList } from '../api/endpoints.js';
import { StageBadge } from '../components/StageBadge.js';
import { fmtDateTime } from '../lib/format.js';

export function DashboardPage() {
  const statsQuery = useQuery({
    queryKey: ['pipeline-stats'],
    queryFn: fetchPipelineStats,
  });
  // Admin-only endpoint: non-admins simply don't get the sync-health card.
  const statusQuery = useQuery({
    queryKey: ['indeed-status'],
    queryFn: fetchIndeedStatus,
    retry: false,
  });

  const stats = statsQuery.data;
  const funnel = stats?.funnel ?? [];
  // Render funnel in canonical stage order, tolerating missing stages.
  const funnelByKey = new Map(funnel.map((f) => [f.stageKey, f]));
  const recent = stats?.recentApplications ?? [];
  const status = statusQuery.data;
  const runs = lastRunsList(status);

  return (
    <div className="page">
      <header className="page__header">
        <h1>Dashboard</h1>
      </header>

      {statsQuery.isLoading && <div className="spinner" aria-label="Loading stats" />}
      {statsQuery.isError && (
        <div className="alert alert--error">Failed to load pipeline stats.</div>
      )}

      {stats && (
        <>
          {stats.totals && (
            <section className="stat-grid">
              <div className="stat-card">
                <span className="stat-card__value">{stats.totals.jobs ?? 0}</span>
                <span className="stat-card__label">Jobs</span>
              </div>
              <div className="stat-card">
                <span className="stat-card__value">{stats.totals.openJobs ?? 0}</span>
                <span className="stat-card__label">Open jobs</span>
              </div>
              <div className="stat-card">
                <span className="stat-card__value">{stats.totals.candidates ?? 0}</span>
                <span className="stat-card__label">Candidates</span>
              </div>
              <div className="stat-card">
                <span className="stat-card__value">{stats.totals.applications ?? 0}</span>
                <span className="stat-card__label">Applications</span>
              </div>
            </section>
          )}

          <section className="card">
            <h2 className="card__title">Hiring funnel</h2>
            <div className="funnel">
              {PIPELINE_STAGES.map((stage) => (
                <div key={stage.key} className={`funnel__stage funnel-${stage.key}`}>
                  <span className="funnel__count">{funnelByKey.get(stage.key)?.count ?? 0}</span>
                  <span className="funnel__label">{stage.label}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <section className="card">
        <h2 className="card__title">Recent applications</h2>
        {recent.length === 0 ? (
          <p className="muted">No applications yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job</th>
                <th>Stage</th>
                <th>Applied</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/applications/${a.id}`}>
                      {a.candidate?.fullName ?? 'Unknown candidate'}
                    </Link>
                  </td>
                  <td>{a.jobTitle ?? '—'}</td>
                  <td>
                    <StageBadge stageKey={a.stageKey} />
                  </td>
                  <td>{fmtDateTime(a.appliedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {status && (
        <section className="card">
          <h2 className="card__title">Indeed sync health</h2>
          <div className="kv-row">
            <span className="kv">
              <span className="kv__label">Mode</span>
              <span className={`pill ${status.mode === 'mock' ? 'sync-pending' : 'sync-active'}`}>
                {status.mode}
              </span>
            </span>
            <span className="kv">
              <span className="kv__label">Jobs synced</span>
              <span className="kv__value">{status.jobsSynced}</span>
            </span>
            <span className="kv">
              <span className="kv__label">Pending outbox</span>
              <span className="kv__value">{status.pendingOutbox}</span>
            </span>
          </div>
          {runs.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Finished</th>
                  <th>OK</th>
                  <th>Items</th>
                  <th>Errors</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={`${r.kind}-${r.id}`}>
                    <td>{r.kind}</td>
                    <td>{fmtDateTime(r.finishedAt ?? r.startedAt)}</td>
                    <td>{r.ok == null ? '…' : r.ok ? 'yes' : 'no'}</td>
                    <td>{r.items}</td>
                    <td>{r.errors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
